import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import Footer from './components/Footer';
import LocaleSync from './components/LocaleSync';
import HomePage from './pages/HomePage';
import ContentPage from './pages/ContentPage';
import ArticlesList from './pages/ArticlesList';
import SectionIndexPage from './pages/SectionIndexPage';
import QuotePage from './pages/QuotePage';
import NotFound from './pages/NotFound';
import Matomo from './components/Matomo';
import contentPages from './generated/content-pages.json';
import './App.css';

const CONTENT_SECTIONS = ['hub', 'articles', 'comparatifs', 'solutions', 'secteurs'];
const EN_CONTENT_SECTIONS = ['solutions-en', 'comparatifs-en'];
const SECTION_INDEXES = ['solutions', 'secteurs', 'comparatifs'];
const EN_SECTION_INDEXES = [
  { section: 'solutions-en', path: '/en/solutions/' },
  { section: 'comparatifs-en', path: '/en/comparisons/' },
];
const HUB_SLUGS = Object.keys(contentPages.hub || {});
const EN_HUB_SLUGS = Object.keys(contentPages['hub-en'] || {});
const PAGE_SLUGS = Object.keys(contentPages.pages || {});

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <Matomo />
          <LocaleSync />
          <div className="App">
            <Header />
            <Routes>
              <Route path="/" element={<HomePage locale="fr" />} />
              <Route path="/en" element={<Navigate to="/en/" replace />} />
              <Route path="/en/" element={<HomePage locale="en" />} />
              {HUB_SLUGS.map((slug) => (
                <Route
                  key={`hub-${slug}-redirect`}
                  path={`/${slug}`}
                  element={<Navigate to={`/${slug}/`} replace />}
                />
              ))}
              {HUB_SLUGS.map((hubSlug) => (
                <Route
                  key={`hub-${hubSlug}`}
                  path={`/${hubSlug}/`}
                  element={<ContentPage section="hub" slug={hubSlug} />}
                />
              ))}
              {EN_HUB_SLUGS.map((slug) => (
                <Route
                  key={`hub-en-${slug}-redirect`}
                  path={`/en/${slug}`}
                  element={<Navigate to={`/en/${slug}/`} replace />}
                />
              ))}
              {EN_HUB_SLUGS.map((hubSlug) => (
                <Route
                  key={`hub-en-${hubSlug}`}
                  path={`/en/${hubSlug}/`}
                  element={<ContentPage section="hub-en" slug={hubSlug} />}
                />
              ))}
              {PAGE_SLUGS.map((slug) => (
                <Route
                  key={`page-${slug}-redirect`}
                  path={`/${slug}`}
                  element={<Navigate to={`/${slug}/`} replace />}
                />
              ))}
              {PAGE_SLUGS.map((slug) => (
                <Route
                  key={`page-${slug}`}
                  path={`/${slug}/`}
                  element={<ContentPage section="pages" slug={slug} />}
                />
              ))}
              <Route path="/articles" element={<Navigate to="/articles/" replace />} />
              <Route path="/articles/" element={<ArticlesList />} />
              {SECTION_INDEXES.map((section) => (
                <Route
                  key={`${section}-redirect`}
                  path={`/${section}`}
                  element={<Navigate to={`/${section}/`} replace />}
                />
              ))}
              {SECTION_INDEXES.map((section) => (
                <Route
                  key={`${section}-index`}
                  path={`/${section}/`}
                  element={<SectionIndexPage section={section} locale="fr" />}
                />
              ))}
              {EN_SECTION_INDEXES.map(({ section, path }) => (
                <Route
                  key={`${section}-redirect`}
                  path={path.replace(/\/$/, '')}
                  element={<Navigate to={path} replace />}
                />
              ))}
              {EN_SECTION_INDEXES.map(({ section, path }) => (
                <Route
                  key={`${section}-index`}
                  path={path}
                  element={<SectionIndexPage section={section} locale="en" />}
                />
              ))}
              {CONTENT_SECTIONS.filter((section) => section !== 'hub' && section !== 'articles').map((section) => (
                <Route
                  key={section}
                  path={`/${section}/:slug/`}
                  element={<ContentPage section={section} />}
                />
              ))}
              {EN_CONTENT_SECTIONS.map((section) => {
                const urlSegment = section === 'comparatifs-en' ? 'comparisons' : 'solutions';
                return (
                  <Route
                    key={section}
                    path={`/en/${urlSegment}/:slug/`}
                    element={<ContentPage section={section} />}
                  />
                );
              })}
              <Route
                path="/articles/:slug/"
                element={<ContentPage section="articles" />}
              />
              <Route path="/devis" element={<QuotePage withTrailingSlash={false} />} />
              <Route path="/devis/" element={<QuotePage locale="fr" />} />
              <Route path="/technologies" element={<Navigate to="/webrtc/" replace />} />
              <Route path="/technologies/" element={<Navigate to="/webrtc/" replace />} />
              <Route path="/technologies/*" element={<Navigate to="/webrtc/" replace />} />
              <Route path="/en/quote" element={<Navigate to="/en/quote/" replace />} />
              <Route path="/en/quote/" element={<QuotePage locale="en" />} />
              <Route path="/blog/*" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Footer />
          </div>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
